import { useEffect } from 'react';

/**
 * Gives every cell of a `tbl-stack` table the label of its column.
 *
 * On a phone those tables become a list of cards (styles.css), and a card has
 * no header row: each value needs its column's name drawn in front of it, from
 * `data-label`. Writing that onto every cell of every table by hand would repeat
 * each heading twice in the source and drift the first time a column is added,
 * so it is read off the table's own header instead.
 *
 * Done on the DOM rather than in each table's JSX because the tables come and
 * go - a pop-up opens, a page of rows changes - and a pop-up is not inside the
 * app shell. One observer on the document covers all of them.
 *
 * What it leaves alone: the first cell, which is the card's heading (or the cell
 * marked `stack-title` when the first column is something else, like a row number,
 * which `stack-hide` drops from the card); a cell that already carries a `data-label` (set by hand, including an empty one for
 * an actions cell); a cell with a `colspan`, which does not sit under one
 * heading; and a column with no heading text.
 */
function label(table: Element): void {
  const heads = Array.from(table.querySelectorAll('thead th')).map(
    (th) => th.textContent?.trim() ?? '',
  );
  for (const row of Array.from(table.querySelectorAll('tbody tr, tfoot tr'))) {
    Array.from(row.children).forEach((cell, index) => {
      if (index === 0 || cell.hasAttribute('data-label')) return;
      if (cell.classList.contains('stack-title') || cell.classList.contains('stack-hide')) return;
      if ((cell as HTMLTableCellElement).colSpan > 1) return;
      const heading = heads[index];
      if (heading) cell.setAttribute('data-label', heading);
    });
  }
}

const labelAll = () => document.querySelectorAll('table.tbl-stack').forEach(label);

export function useStackedTableLabels(): void {
  useEffect(() => {
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(labelAll);
    };
    // Only added or removed nodes: setting the attribute is not itself a change
    // worth reacting to, which keeps this from chasing its own tail.
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    labelAll();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);
}
